// Autoplay: one turn every 1 / speed seconds until the as-of date, a pause, or a jump.
//
//   start ─► tick ─► nextTurn() ─► wait out the rest of the interval ─► tick …
//
// The wait is measured from the tick's start, so a turn that takes 300 ms at speed 2 (500 ms)
// waits 200 ms, and one that takes longer than the interval does not stack behind itself.

import type { Store } from './state.ts';

const MS_PER_S = 1000;
/** Fastest and slowest turns per second the loop accepts. */
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 30;

export interface Playback {
  start(): void;
  stop(): void;
}

export function clampSpeed(turnsPerSecond: number): number {
  if (!Number.isFinite(turnsPerSecond)) {
    return MIN_SPEED;
  }
  return Math.min(MAX_SPEED, Math.max(MIN_SPEED, turnsPerSecond));
}

export function createPlayback(store: Store, advance: () => Promise<void>): Playback {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  // Each start owns a generation: a loop still awaiting a turn when its run was stopped (and
  // maybe restarted) sees a newer generation and ends, instead of running beside the new one.
  let generation = 0;

  function stop(): void {
    running = false;
    generation += 1;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    store.playing.value = false;
  }

  async function tick(gen: number): Promise<void> {
    if (gen !== generation) {
      return;
    }
    if (store.date.peek() >= store.asOf.peek()) {
      stop();
      return;
    }

    const began = performance.now();
    await advance();
    if (gen !== generation) {
      return;
    }
    if (store.date.peek() >= store.asOf.peek()) {
      stop();
      return;
    }

    const interval = MS_PER_S / store.speed.peek();
    timer = setTimeout(() => void tick(gen), Math.max(0, interval - (performance.now() - began)));
  }

  return {
    start() {
      if (running || !store.leader.peek() || store.date.peek() >= store.asOf.peek()) {
        return;
      }
      running = true;
      generation += 1;
      store.playing.value = true;
      void tick(generation);
    },
    stop,
  };
}
